import { computed, reactive, unref } from 'vue';
import { store } from '@/store';
import { ConditionPickerOption } from '@/shared/components/conditions/models/conditions';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { InputVariableViewModel } from '@/features/bots/models/designer';
import { ConditionComponents } from '@shared/components/conditions/configComponents/conditionComponents';
import { useConditions } from '@shared/composables/useConditions';

export enum DecisionConditionsAttributeType {
  TimeOfDay = 'system.currentTimeUtc',
}

export function useDecisionConditions() {
  // Reactive state for contextual conditions
  const state = reactive({
    contextualConditions: [] as ConditionPickerOption[],
  });

  const base = useConditions(store.getters.contactAttributes);

  // Override conditions to include contextual conditions
  const conditions = computed(() => {
    const attributes = unref(base.conditions.value) || [];
    return [...attributes, ...state.contextualConditions];
  });

  // Method to set contextual conditions
  const setContextualConditions = (contextualConditions: ConditionPickerOption[]) => {
    state.contextualConditions = contextualConditions;
  };

  // Override isConditionValid
  const isConditionValid = (condition: ConditionViewModel): boolean => {
    if (condition.attribute.startsWith('flow')) {
      const flowVariables = store.getters.flowVariables;
      const flowVariable = flowVariables.find(
        (fv: InputVariableViewModel) => fv.name === condition.attribute.split('.')[1],
      );
      return !!flowVariable;
    }
    return base.isConditionValid(condition);
  };

  // Override getConfigComponent
  const getConfigComponent = (condition: ConditionViewModel) => {
    if (condition.attribute === DecisionConditionsAttributeType.TimeOfDay) {
      return ConditionComponents.TimeOfDayCondition;
    }
    return base.getConfigComponent(condition);
  };

  // Override getFormattedDescription
  const getFormattedDescription = (condition: ConditionViewModel) => {
    if (!condition.invalid && condition.attribute === DecisionConditionsAttributeType.TimeOfDay) {
      if (!condition.value || condition.value === 'bot.openingTimes') {
        return `Time of day is ${condition.result ? 'in hours' : 'out of hours'}`;
      } else {
        return 'Time of day is in custom schedule';
      }
    }
    return base.getFormattedDescription(condition);
  };

  return {
    ...base, // Spread base functionality
    conditions, // Overridden with contextual conditions
    visibleConditions: conditions, // In this case, visibleConditions same as conditions
    setContextualConditions,
    isConditionValid,
    getConfigComponent,
    getFormattedDescription,
  };
}
